import React, { useState, useEffect, useCallback, useRef } from 'react';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Eye, EyeOff } from 'lucide-react';

import API_BASE from './apiBase';
import DSARForm from './dps';
import DSARLogin from './DSARLogin';
import DSARDashboardUser from './DSARDashboardUser';
import TrialConsentForm from './TrialConsentForm';
import dpdpConsentConfig from './ConsentBannerconfig';
import { DpdpConsentPreview } from './ConsentBannerPage';
import '../css/App.css';

// ─── SweetAlert Global Fix ───────────────────────────
const MySwal = Swal.mixin({
  timer: null,
  timerProgressBar: false,
  showConfirmButton: true,
  allowOutsideClick: false,
  allowEscapeKey: false,
});

// ─── Map any error → { title, message, footer } ──────────────────────────────
const parseLoginError = (err) => {
  if (!err?.response) {
    if (err?.code === 'ECONNABORTED' || err?.message?.toLowerCase().includes('timeout')) {
      return { title: 'Request Timed Out', message: 'The server took too long to respond. Please check your internet connection and try again.', footer: '' };
    }
    if (err?.message?.toLowerCase().includes('network')) {
      return { title: 'Network Error', message: 'Unable to reach the server. Please check your internet connection.', footer: '' };
    }
    return { title: 'Server Unreachable', message: 'Could not connect to the server. Please try again later.', footer: '' };
  }
  const status = err.response.status;
  const detail = err.response.data?.detail || err.response.data?.message || err.response.data?.error || '';
  switch (status) {
    case 400: return { title: 'Invalid Request', message: detail || 'The request was invalid. Please check your inputs and try again.', footer: '' };
    case 401: return { title: 'Invalid Credentials', message: 'Incorrect email or password. Please try again.', footer: '' };
    case 403: return { title: 'Access Denied', message: detail || 'Your account is inactive or subscription has expired.', footer: '<span style="color:#2563eb">Contact sales@whitehats.com</span>' };
    case 404: return { title: 'Account Not Found', message: 'No account exists with this email address. Please check and try again.', footer: '' };
    case 422: return { title: 'Validation Error', message: detail || 'Some fields are invalid. Please check your inputs.', footer: '' };
    case 429: return { title: 'Too Many Attempts', message: 'Too many login attempts. Please wait a few minutes before trying again.', footer: '' };
    case 500: return { title: 'Server Error', message: 'An internal server error occurred. Please try again later.', footer: '<span style="color:#2563eb">If this persists, contact support@whitehats.com</span>' };
    case 502: case 503: case 504: return { title: 'Service Unavailable', message: 'The server is temporarily unavailable. Please try again in a few moments.', footer: '' };
    default: return { title: 'Login Failed', message: detail || `An unexpected error occurred (code: ${status}). Please try again.`, footer: '' };
  }
};


function LoginForm({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showDSARLogin, setShowDSARLogin] = useState(false);
  const [showDSARDashboard, setShowDSARDashboard] = useState(false);
  const [showDSARForm, setShowDSARForm] = useState(false);
  const [showTrialForm, setShowTrialForm] = useState(false);
  const [showConsentWidget, setShowConsentWidget] = useState(false);
  const formRef = useRef(null);

  // ─── Use refs to track alert open state — prevents re-render from closing alert ──
  const alertOpenRef = useRef(false);

  const ssoLoginUrl = `${(API_BASE || '').replace(/\/+$/, '')}${(API_BASE || '').replace(/\/+$/, '').endsWith('/api') ? '/v1/sso-login' : '/api/v1/sso-login'}`;

  useEffect(() => {
    try {
      const expiredMsg = sessionStorage.getItem('session_expired_message');
      if (expiredMsg) {
        sessionStorage.removeItem('session_expired_message');
        Swal.fire({
          icon: 'warning',
          title: 'Session Expired',
          text: expiredMsg,
          confirmButtonText: 'OK',
          confirmButtonColor: '#2563eb',
          allowOutsideClick: false,
          customClass: { popup: 'swal-popup-custom' }
        });
      }
    } catch (e) { }
  }, []);

  // ─── autofill sync — only runs once on mount, not on every keystroke ─────────
  useEffect(() => {
    const syncOnce = () => {
      const emailEl = document.getElementById('email');
      const passwordEl = document.getElementById('password');
      if (emailEl?.value) setEmail(emailEl.value);
      if (passwordEl?.value) setPassword(passwordEl.value);
    };
    syncOnce();
    const timeout = window.setTimeout(syncOnce, 400);
    return () => window.clearTimeout(timeout);
  }, []); // ← empty deps: runs once only, no re-render loop

  // ─── main login ──────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }

    if (loading || alertOpenRef.current) return;

    setError('');
    setLoading(true);

    // ── Helper: show error alert — NO state change until user clicks OK ───────
    const fireError = async (title, message, footer = '') => {

      setLoading(false);
      alertOpenRef.current = true;

      await MySwal.fire({
        icon: 'error',
        title,
        html: message,
        footer: footer || '',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc2626',

        allowOutsideClick: false,
        allowEscapeKey: false,

        timer: null,
        timerProgressBar: false,

        didOpen: () => {
          Swal.hideLoading();
        }
      });

      alertOpenRef.current = false;
      setError(message);

    };

    // ── Blur inputs (iOS autofill fix) ────────────────────────────────────────
    try {
      if (formRef.current)
        formRef.current.querySelectorAll('input').forEach(inp => inp.blur?.());
    } catch (_) { }

    await new Promise(r => setTimeout(r, 50));

    const emailValue = email || document.getElementById('email')?.value || '';
    const passwordValue = password || document.getElementById('password')?.value || '';

    // ── reCAPTCHA check ───────────────────────────────────────────────────────
    let captchaResponse = '';
    try { captchaResponse = window.grecaptcha?.getResponse?.() || ''; } catch (_) { }
    if (!captchaResponse) {
      captchaResponse = document.querySelector('textarea[name="g-recaptcha-response"]')?.value || '';
    }

    if (!captchaResponse) {
      fireError('reCAPTCHA Required', 'Please complete the reCAPTCHA challenge before signing in.');
      return;
    }

    // ── Build request body ────────────────────────────────────────────────────
    const body = {
      email: emailValue.trim().toLowerCase(),
      password: passwordValue,
      'g-recaptcha-response': captchaResponse,
    };

    const baseURL = (API_BASE || '').replace(/\/+$/, '');
    const endpoints = ['/user/login', '/manager/login', '/admin/login'];
    let successRes = null;

    for (const path of endpoints) {
      const url = `${baseURL}${path}`;
      try {
        const res = await axios.post(url, body, { skipRedirectOn401: true });
        successRes = res;
        break;
      } catch (axiosErr) {
        const status = axiosErr?.response?.status;

        console.error(`[LoginForm] ${path} →`, status ?? 'NO_RESPONSE', axiosErr?.message);

        if (!status) {
          const isTimeout = axiosErr?.code === 'ECONNABORTED' || /timeout/i.test(axiosErr?.message || '');
          if (isTimeout) {
            fireError('Request Timed Out', 'The server took too long to respond. Please check your connection and try again.');
          } else {
            fireError('Network Error', 'Unable to reach the server. Please check your internet connection and try again.');
          }
          return;
        }

        if (status === 404) continue;

        const detail = axiosErr?.response?.data?.detail
          || axiosErr?.response?.data?.message
          || axiosErr?.response?.data?.error
          || '';

        if (status === 401) {
          fireError('Invalid Credentials', 'Incorrect email or password. Please try again.');
        } else if (status === 403) {
          // fireError('Access Denied', detail || 'Your account is inactive or your subscription has expired.', '<span style="color:#2563eb">Contact sales@whitehats.com</span>');
          const inactive = /inactive/i.test(detail || '');
          if (inactive) {
            fireError('Access Expired', 'Your access has expired. Please contact your manager.', '');
          } else {
            fireError('Access Denied', detail || 'Your account is inactive or your subscription has expired.', '<span style="color:#2563eb">Contact sales@whitehats.com</span>');
          }
        } else if (status === 422) {
          fireError('Validation Error', detail || 'Some fields are invalid. Please check your inputs.');
        } else if (status === 429) {
          fireError('Too Many Attempts', 'Too many login attempts. Please wait a few minutes and try again.');
        } else if (status >= 500) {
          fireError('Server Error', 'An internal server error occurred. Please try again later.', '<span style="color:#2563eb">If this continues, contact support@whitehats.com</span>');
        } else {
          fireError('Login Failed', detail || `Unexpected error (${status}). Please try again.`);
        }
        return;
      }
    }

    if (!successRes) {
      fireError('Account Not Found', 'No account found with this email address. Please check and try again.');
      return;
    }

    // alretOpenRef.current = true;
    setLoading(false);
    if (onLogin) onLogin(successRes.data);
    MySwal.fire({
      title: 'Login Successful',
      text: 'Welcome to Consent Management Portal',
      icon: 'success',
      confirmButtonColor: '#2563eb',
      confirmButtonText: 'OK',
      timer: 1400,
      timerProgressBar: true,
      showConfirmButton: false,
      allowOutsideClick: true,
      allowEscapeKey: true,
      //   showConfirmButton: true,
      //   allowOutsideClick: false,
      //   allowEscapeKey: false,
      // }).then(() => {
      //   alertOpenRef.current = false;
      //   setLoading(false);
      //   if (onLogin) onLogin(successRes.data);
    });
  };

  // ─── reCAPTCHA ───────────────────────────────────────────────────────────────
  const SITE_KEY = process.env.REACT_APP_RECAPTCHA_PUBLIC_KEY || window.RECAPTCHA_SITE_KEY || '';
  const recaptchaRef = useRef(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent || '');

  const renderRecaptcha = useCallback(() => {
    try {
      if (window.grecaptcha && recaptchaRef.current) {
        window.grecaptcha.render(recaptchaRef.current, { sitekey: SITE_KEY });
        recaptchaRef.current.dataset.rendered = '1';
        setRecaptchaLoaded(true);
        setRecaptchaError(false);
      }
    } catch (err) {
      setRecaptchaError(true);
      setRecaptchaLoaded(false);
      console.error('recaptcha render error', err);
    }
  }, [SITE_KEY]);

  useEffect(() => {
    if (!SITE_KEY) return;
    setRecaptchaLoaded(false);
    setRecaptchaError(false);
    window.__recaptchaOnLoad = () => renderRecaptcha();
    if (!document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]')) {
      const s = document.createElement('script');
      s.src = 'https://www.google.com/recaptcha/api.js?onload=__recaptchaOnLoad&render=explicit';
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    } else {
      setTimeout(() => renderRecaptcha(), isIOS ? 300 : 0);
    }
    if (isIOS) setTimeout(() => renderRecaptcha(), 600);
    return () => { try { delete window.__recaptchaOnLoad; } catch (_) { } };
  }, [SITE_KEY, renderRecaptcha, isIOS]);

  useEffect(() => {
    if (!SITE_KEY) {
      console.error('[LoginForm] reCAPTCHA site key is missing. Set REACT_APP_RECAPTCHA_PUBLIC_KEY before building the frontend.');
    }
  }, [SITE_KEY]);

  // ─── SSO auto-login ───────────────────────────────────────────────────────────
  useEffect(() => {
    console.log('LoginForm init', { API_BASE, SITE_KEY });
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search || '');

        // 1) Check for sso_error in URL query string
        const ssoError = params.get('sso_error') || params.get('error');
        if (ssoError) {
          const publicSsoError = {
            missing_token: 'Your OneDPDP login link is missing a token. Please try again from OneDPDP.',
            invalid_token: 'Your OneDPDP login link is invalid or has expired. Please try again from OneDPDP.',
            account_error: 'Unable to create your Consent account. Please try again.',
            access_denied: 'You do not have access to the Consent Website through OneDPDP.',
            session_error: 'Unable to create your Consent session. Please try again.'
          }[ssoError] || 'Unable to sign you in through OneDPDP. Please try again.';
          try {
            const url = new URL(window.location.href);
            ['sso_error', 'error'].forEach(k => url.searchParams.delete(k));
            window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
          } catch (_) { }
          MySwal.fire({
            icon: 'error',
            title: 'SSO Login Failed',
            // text: decodeURIComponent(ssoError),
            text: publicSsoError,
            confirmButtonText: 'OK'
          });
          return;
        }

        // 2) Check for direct session_key / sso_token in URL (e.g. /sso-login?session_key=...)
        let sessionKey = params.get('session_key') || params.get('sessionKey') || params.get('sso_token');
        if (sessionKey) {
          try {
            // const ssourl = `${(API_BASE || '').replace(/\/+$/, '')}/sso-login`;
            // const res = await axios.get(ssourl, { params: { session_key: sessionKey, json: 1 } });
            // const res = await axios.get(ssoLoginUrl, { params: { session_key: sessionKey, json: 1 } });
            const res = await axios.get(ssoLoginUrl, {
              params: { session_key: sessionKey, json: 1 },
              // skipRedirectOn401: true,
              skipRedirectOn401: true,
              withCredentials: true
            });
            if (res?.data?.success && res?.data?.token) {
              const newToken = res.data.token;
              const u = res.data.user;
              localStorage.setItem('token', newToken);
              localStorage.setItem('last_active_at', String(Date.now()));
              if (u) localStorage.setItem('user', JSON.stringify(u));
              try {
                const url = new URL(window.location.href);
                ['session_key', 'sessionKey', 'sso_token', 'token'].forEach(k => url.searchParams.delete(k));
                window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
              } catch (_) { }
              if (onLogin && u) {
                onLogin({ token: newToken, user: u, tenant_id: u?.tenant_id || null });
                return;
              }
            } else {
              MySwal.fire({
                icon: 'error',
                title: 'SSO Login Failed',
                text: res?.data?.error || 'Invalid or expired SSO token.',
                confirmButtonText: 'OK'
              });
              return;
            }
          } catch (err) {
            console.error('SSO exchange error:', err);
            const errMsg = err?.response?.data?.error || err?.response?.data?.detail || 'SSO authentication failed. Please try again.';
            MySwal.fire({
              icon: 'error',
              title: 'SSO Login Failed',
              text: errMsg,
              confirmButtonText: 'OK'
            });
            return;
          }
        }

        // 3) If the SPA received the callback route, exchange the OneDPDP token
        // once on the Consent backend. The normal backend callback redirect
        // remains the primary path and never reaches this branch.
        let token = params.get('token') || params.get('access_token') || params.get('session_token');

        const callbackPath = /\/sso\/callback\/?$/i.test(window.location.pathname);
        if (token && callbackPath) {
          try {
            const res = await axios.get(ssoLoginUrl, {
              params: { token, json: 1 },
              timeout: 8000,
              // skipRedirectOn401: true,
              skipRedirectOn401: true,
              withCredentials: true
            });
            // const res = await axios.get(ssoLoginUrl, { params: { token, json: 1 }, timeout: 8000 });
            if (!res?.data?.success || !res?.data?.token) {
              throw new Error('SSO token exchange failed');
            }
            token = res.data.token;
            if (res.data.user) localStorage.setItem('user', JSON.stringify(res.data.user));
            try {
              const url = new URL(window.location.href);
              ['token', 'access_token', 'session_token'].forEach(k => url.searchParams.delete(k));
              window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
            } catch (_) { }
          } catch (err) {
            console.error('SSO callback exchange failed:', err?.response?.status || err?.message);
            MySwal.fire({
              icon: 'error',
              title: 'Unable to sign you in',
              text: 'Your OneDPDP login link is invalid or has expired. Please try again from OneDPDP.',
              confirmButtonText: 'OK'
            });
            return;
          }
        }

        // Support DSAR encrypted token parameter 't' (from revoke emails)
        try {
          const dsarT = params.get('t');
          if (dsarT) {
            try {
              const verifyUrl = `${(API_BASE || '').replace(/\/+$/, '')}/user/dsar/verify`;
              const res = await axios.get(verifyUrl, { params: { t: dsarT } });
              if (res?.data?.success) {
                try { window.__DSAR_PREFILL__ = res.data; } catch (_) { }
                try { localStorage.setItem('dsar_prefill', JSON.stringify(res.data)); } catch (_) { }
                setShowDSARLogin(true);
              } else {
                setShowDSARLogin(true);
              }
            } catch (e) {
              console.warn('DSAR verify failed', e);
              setShowDSARLogin(true);
            }
            return;
          }
        } catch (_) { }

        if (!token) {
          try {
            for (const c of (document.cookie || '').split(';').map(s => s.trim())) {
              if (c.startsWith('access_token=')) { token = decodeURIComponent(c.split('=')[1] || ''); break; }
            }
          } catch (_) { }
        }

        if (token) {
          try {
            localStorage.setItem('token', token);
            localStorage.setItem('last_active_at', String(Date.now()));
            try {
              const url = new URL(window.location.href);
              ['token', 'access_token', 'session_token'].forEach(k => url.searchParams.delete(k));
              window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
            } catch (_) { }
            try {
              // const res = await axios.get(`${(API_BASE || '').replace(/\/+$/, '')}/user/status`, {timeout:5000});
              const res = await axios.get(`${(API_BASE || '').replace(/\/+$/, '')}/user/status`, {
                timeout: 5000,
                withCredentials: true,
                skipRedirectOn401: true
              });
              // const u = res?.data?.user || null;
              let u = res?.data?.user || null;
              if (!u) {
                try {
                  const savedUser = localStorage.getItem('user');
                  if (savedUser) u = JSON.parse(savedUser);
                } catch (_) { }
              }
              if (u && onLogin) {
                onLogin({
                  token,
                  user: {
                    id: u.id,
                    email: u.email,
                    tenant_id: u.tenant_id || u.tenantId || null,
                    role: u.role || 'user',
                    full_name: u.full_name || u.fullName || u.name || u.email
                  },
                  tenant_id: u.tenant_id || u.tenantId || null
                });
                return;
              }
            } catch (err) {
              // console.error('SSO token verification failed:', err);
              console.error('SSO session verification failed:', {
                status: err?.response?.status || null,
                detail: err?.response?.data?.detail || err?.response?.data?.error || null,
                code: err?.code || null
              });
              try { localStorage.removeItem('token'); } catch (__) { }
              const errMsg = err?.response?.data?.detail || err?.response?.data?.error || 'Your SSO session has expired or is invalid.';
              MySwal.fire({
                icon: 'error',
                title: 'SSO Session Invalid',
                text: errMsg,
                confirmButtonText: 'OK'
              });
            }
          } catch (_) { }
        }
      } catch (_) { }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onSubmitted = () => {
      setShowDSARForm(false);
      try { MySwal.fire({ text: 'DSAR submitted — dashboard will update', icon: 'success' }); } catch (_) { }
    };
    window.addEventListener('dsar:submitted', onSubmitted);
    return () => window.removeEventListener('dsar:submitted', onSubmitted);
  }, []);

  // ─── DSAR login success ───────────────────────────────────────────────────────
  const handleDSARLoginSuccess = ({ type, email: dsarEmail }) => {
    setShowDSARLogin(false);
    setShowDSARDashboard(true);
  };

  // ─── If DSAR dashboard is active, render it full-screen ──────────────────────
  if (showDSARDashboard) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#f4f6fa', overflowY: 'auto' }}>
        <DSARDashboardUser />
      </div>
    );
  }

  // ─── Normal login page ────────────────────────────────────────────────────────
  return (
    <>
      <div className="split">
        {/* LEFT - LOGIN FORM */}
        <section className="pane left">
          <div className="card" role="main">
            <div className="mobile-logo-img">
              <img
                src="/static/img/WhitehatsLogo2.webp" alt="i-ConsentO logo"
                onError={e => { e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="60"%3E%3Crect fill="%23ffffff" width="200" height="60" rx="6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%232563eb" font-size="18" font-family="Arial" font-weight="700"%3EWhitehats%3C/text%3E%3C/svg%3E'; }}
              />
            </div>

            <div className="brand">
              <div className="logo">C</div>
              <div><h1>Consent Management</h1></div>
            </div>

            <form ref={formRef} onSubmit={handleSubmit} autoComplete="on">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input id="email" name="email" type="email" autoComplete="email"
                  placeholder="you@company.com" value={email}
                  onChange={e => setEmail(e.target.value)} required autoFocus />
              </div>


              <div className="form-group">
                <label htmlFor="password">Password</label>

                <div style={{ position: "relative" }}>

                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    style={{
                      width: "100%",
                      paddingRight: "45px"
                    }}
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => !prev)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: "none",
                      outline: "none",
                      boxShadow: "none",
                      background: "transparent",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                      color: "#64748b"
                    }}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>

                </div>
              </div>

              {SITE_KEY && (
                <div style={{ minHeight: 90, marginBottom: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div ref={recaptchaRef} className="g-recaptcha" data-sitekey={SITE_KEY}
                    style={{ minHeight: 78, width: '100%', margin: '0 auto' }} />
                  {recaptchaError && (
                    <div style={{ color: 'red', marginTop: 6 }}>
                      reCAPTCHA failed to load.{' '}
                      <button type="button" onClick={renderRecaptcha}
                        style={{ color: '#2563eb', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button>
                    </div>
                  )}
                  {isIOS && !recaptchaLoaded && !recaptchaError && (
                    <div style={{ color: '#b91c1c', marginTop: 6, fontSize: 13 }}>
                      If reCAPTCHA does not appear, reload the page or tap Retry.<br />
                      iPhone/iPad browsers may block reCAPTCHA.
                    </div>
                  )}
                </div>
              )}

              <div className="actions">
                <button type="button" className="primary" disabled={loading}
                  onClick={handleSubmit}
                  style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}>
                  {loading ? 'Signing in...' : 'Sign in'}
                </button>
              </div>

              {/* {error && <div className="error-box">{error}</div>} */}

              <div className="divider" aria-hidden="true" />

              <div className="button-row-50">
                <button type="button" className="dsar" onClick={() => setShowDSARLogin(true)}>
                  Open DSAR Form
                </button>
                <button type="button" className="dsar" onClick={() => setShowTrialForm(true)}>
                  Request Access
                </button>
                <button
                  type="button"
                  className="dsar"
                  onClick={() => { window.location.href = '/customer-consent-login'; }}
                >
                  Customer Consent
                </button>
                <button
                  type="button"
                  className="dsar"
                  onClick={() => setShowConsentWidget(true)}
                >
                  Demo Banner
                </button>
                {/* <button
                  type="button"
                  className="dsar"
                  onClick={() => { window.location.href = '/local-sso-login'; }}
                >
                  Local SSO Login
                </button> */}
              </div>
            </form>
          </div>
        </section>

        {/* RIGHT - BRANDING */}
        <aside className="pane right">
          <div className="top-logo">
            <img src="/static/img/WhitehatsLogo2.webp" alt="i-ConsentO logo"
              onError={e => { e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="60"%3E%3Crect fill="%23ffffff" width="200" height="60" rx="6"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%232563eb" font-size="18" font-family="Arial" font-weight="700"%3EWhitehats%3C/text%3E%3C/svg%3E'; }}
            />
          </div>
          <div className="right-inner">
            <div className="brand-ghost">CONSENT</div>
            <div className="dpdp">
              <span className="badge">DPDP</span>
              <small>Data Protection &amp; Privacy (DPDP) compliant</small>
            </div>
            <h2>Transparent, Simple Consent</h2>
            <p>Manage user permissions and stay compliant with an elegant dashboard.</p>
            <div className="illus" aria-hidden="true">
              <div className="tile">
                <svg width="100%" height="80" viewBox="0 0 200 80" xmlns="http://www.w3.org/2000/svg">
                  <rect rx="10" width="200" height="80" fill="rgba(255,255,255,0.06)" />
                  <g fill="#fff" opacity="0.9"><circle cx="40" cy="40" r="16" /><rect x="70" y="24" width="84" height="12" rx="6" /></g>
                </svg>
              </div>
              <div className="tile">
                <svg width="100%" height="80" viewBox="0 0 200 80" xmlns="http://www.w3.org/2000/svg">
                  <rect rx="10" width="200" height="80" fill="rgba(255,255,255,0.06)" />
                  <g fill="#fff" opacity="0.9"><rect x="16" y="20" width="168" height="12" rx="6" /><rect x="16" y="44" width="110" height="10" rx="5" /></g>
                </svg>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {showConsentWidget && (
        <div
          className="modal-overlay"
          onClick={() => setShowConsentWidget(false)}
          style={{ background: 'rgba(15, 23, 42, 0.72)', zIndex: 9999, padding: 20 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ position: 'relative', maxHeight: '92vh', overflowY: 'auto', margin: '0 auto' }}
          >
            <button
              type="button"
              onClick={() => setShowConsentWidget(false)}
              style={{ position: 'absolute', top: 14, right: 14, zIndex: 2, border: 'none', background: '#fff', borderRadius: '50%', width: 36, height: 36, cursor: 'pointer', boxShadow: '0 6px 18px rgba(0,0,0,0.16)' }}
              aria-label="Close consent demo"
            >
              ✕
            </button>
            <DpdpConsentPreview
              config={dpdpConsentConfig.getConfig()}
              tenantName="Whitehats"
              onAccept={() => setShowConsentWidget(false)}
              onDecline={() => setShowConsentWidget(false)}
              onPreferences={() => setShowConsentWidget(false)}
            />
          </div>
        </div>
      )}

      {/* ── DSAR LOGIN MODAL ── */}
      {showDSARLogin && (
        <div className="modal-overlay" onClick={() => setShowDSARLogin(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}
            style={{ maxWidth: 480, width: '95%', padding: 0, background: 'transparent', boxShadow: 'none' }}>
            <button onClick={() => setShowDSARLogin(false)} className="modal-close"
              style={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}>✕</button>
            <DSARLogin onLoginSuccess={handleDSARLoginSuccess} />
          </div>
        </div>
      )}

      {/* ── DSAR FORM MODAL (legacy) ── */}
      {showDSARForm && (
        <div className="modal-overlay" onClick={() => setShowDSARForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowDSARForm(false)} className="modal-close">✕</button>
            <DSARForm showEmbed={false} />
          </div>
        </div>
      )}

      {/* ── TRIAL FORM MODAL ── */}
      {showTrialForm && (
        <div className="modal-overlay" onClick={() => setShowTrialForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowTrialForm(false)} className="modal-close">✕</button>
            <TrialConsentForm onClose={() => setShowTrialForm(false)} />
          </div>
        </div>
      )}

      <footer style={{
        width: '50%', textAlign: 'center', padding: '4px 8px', color: 'Black',
        fontSize: '13px', position: 'fixed', left: 0, right: 0, bottom: 0,
        background: 'transparent', zIndex: 1, pointerEvents: 'none',
      }}>
        © Whitehats Technologies-2026 v1.0.
      </footer>
    </>
  );
}

export default LoginForm;